import { useEffect, useState, type FormEvent } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { X } from 'lucide-react'
import { ApiClientError } from '@/lib/api-client'
import { artistFetch } from '@/lib/artist-api'
import { HUMAN_DEPARTMENTS, type Department } from '@/lib/departments'
import {
  CLASSIFICATION_PRESETS,
  RULE_ACTIONS,
  TEAM_ROLES,
  type Mailbox,
  type RoutingRule,
  type RuleAction,
  type RuleConditions,
  type TeamRole,
} from '@/lib/email-rules'
import { cn } from '@/lib/utils'
import { CreateDepartmentModal } from './CreateDepartmentModal'
import { CreatePersonModal, type DirectoryPerson } from './CreatePersonModal'
import type { DirectoryResponse } from './DirectoryPanel'

export type EditorMode =
  | { type: 'create-branch'; parentRuleId: null; mailboxId: string | null }
  | { type: 'create-subfilter'; parentRuleId: string; mailboxId: string | null }
  | { type: 'edit'; rule: RoutingRule }

interface RuleEditorSheetProps {
  mode: EditorMode
  mailboxes: Mailbox[]
  onClose: () => void
  onSaved: () => void
}

export function RuleEditorSheet({ mode, mailboxes, onClose, onSaved }: RuleEditorSheetProps) {
  const isEdit = mode.type === 'edit'
  const existing = isEdit ? mode.rule : null
  const parentRuleId =
    mode.type === 'create-subfilter' ? mode.parentRuleId : (existing?.parentRuleId ?? null)

  const [name, setName] = useState(existing?.name ?? '')
  const [description, setDescription] = useState(existing?.description ?? '')
  const [priority, setPriority] = useState(existing?.priority ?? (parentRuleId ? 110 : 100))
  const [action, setAction] = useState<RuleAction>(existing?.action ?? 'forward_to_role')
  const [targetRole, setTargetRole] = useState<TeamRole>(existing?.targetRole ?? 'tour_manager')
  const [targetDepartment, setTargetDepartment] = useState<Department | ''>(
    existing?.targetDepartment ?? '',
  )
  const [targetEmail, setTargetEmail] = useState(existing?.targetEmail ?? '')
  const [targetPersonId, setTargetPersonId] = useState(existing?.targetPersonId ?? '')
  const [showCreateDept, setShowCreateDept] = useState(false)
  const [showCreatePerson, setShowCreatePerson] = useState(false)
  const [mailboxId, setMailboxId] = useState(
    existing?.mailboxId ?? (mode.type !== 'edit' ? mode.mailboxId ?? '' : ''),
  )
  const [matchMode, setMatchMode] = useState<'ai' | 'subject' | 'body' | 'domain' | 'any'>(
    () => {
      if (!existing) return parentRuleId ? 'subject' : 'ai'
      if (existing.conditions.classificationType) return 'ai'
      if (existing.conditions.subjectContains) return 'subject'
      if (existing.conditions.bodyContains) return 'body'
      if (existing.conditions.fromDomain) return 'domain'
      return 'any'
    },
  )
  const [classificationType, setClassificationType] = useState(
    existing?.conditions.classificationType ?? 'legal',
  )
  const [keyword, setKeyword] = useState(
    existing?.conditions.subjectContains ??
      existing?.conditions.bodyContains ??
      existing?.conditions.fromDomain ??
      '',
  )
  const [enabled, setEnabled] = useState(existing?.enabled ?? true)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const qc = useQueryClient()
  const { data: directory } = useQuery<DirectoryResponse, ApiClientError>({
    queryKey: ['email-directory'],
    queryFn: () => artistFetch<DirectoryResponse>('/email-directory'),
    enabled: action === 'forward_to_role',
  })
  const directoryDepartments = directory?.departments ?? []
  const pinnablePeople = (directory?.people ?? []).filter((p) => p.scope === 'artist')

  function invalidateDirectory() {
    qc.invalidateQueries({ queryKey: ['email-directory'] })
  }

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  function buildConditions(): RuleConditions {
    if (matchMode === 'any') return {}
    if (matchMode === 'ai') {
      if (classificationType === 'custom') {
        return keyword ? { classificationType: keyword } : {}
      }
      return { classificationType }
    }
    if (matchMode === 'subject') return { subjectContains: keyword }
    if (matchMode === 'body') return { bodyContains: keyword }
    return { fromDomain: keyword }
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      const conditions = buildConditions()
      const body: Record<string, unknown> = {
        name,
        description: description || undefined,
        priority,
        conditions,
        action,
        enabled,
        mailboxId: mailboxId || null,
      }

      if (action === 'forward_to_role') {
        body.targetRole = targetRole
        if (targetDepartment) body.targetDepartment = targetDepartment
        body.targetPersonId = targetPersonId || null
      }
      if (action === 'forward_to_email') body.targetEmail = targetEmail

      if (isEdit) {
        await artistFetch(`/email-rules/${mode.rule.id}`, { method: 'PATCH', body })
      } else {
        if (parentRuleId) body.parentRuleId = parentRuleId
        await artistFetch('/email-rules', { method: 'POST', body })
      }
      onSaved()
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Save failed')
    } finally {
      setSubmitting(false)
    }
  }

  const title =
    mode.type === 'create-branch'
      ? 'Add routing branch'
      : mode.type === 'create-subfilter'
        ? 'Add sub-filter'
        : 'Edit routing rule'

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <button
        type="button"
        className="absolute inset-0 bg-ovation-ink/30 backdrop-blur-[2px]"
        aria-label="Close editor"
        onClick={onClose}
      />
      <aside className="relative flex h-full w-full max-w-md flex-col border-l border-ovation-ink/10 bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-ovation-ink/10 px-5 py-4">
          <div>
            <h2 className="text-base font-semibold text-ovation-ink">{title}</h2>
            <p className="text-xs text-ovation-muted">
              {parentRuleId
                ? 'Refines a parent branch when this filter matches'
                : 'Primary route after AI classification'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1.5 text-ovation-muted hover:bg-ovation-ink/5 hover:text-ovation-ink"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={onSubmit} className="flex flex-1 flex-col overflow-hidden">
          <div className="flex-1 space-y-5 overflow-y-auto px-5 py-4">
            <div>
              <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-ovation-muted">
                Branch name
              </label>
              <input
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={parentRuleId ? 'e.g. Licensing' : 'e.g. Legal'}
                className="w-full rounded-lg border border-ovation-ink/15 px-3 py-2 text-sm"
              />
            </div>

            <div>
              <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-ovation-muted">
                Description (optional)
              </label>
              <input
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full rounded-lg border border-ovation-ink/15 px-3 py-2 text-sm"
              />
            </div>

            <fieldset className="space-y-3 rounded-lg border border-ovation-ink/10 p-4">
              <legend className="px-1 text-xs font-medium uppercase tracking-wider text-ovation-muted">
                When this matches
              </legend>
              <div className="grid grid-cols-2 gap-2">
                {(
                  [
                    ['ai', 'AI category'],
                    ['subject', 'Subject keyword'],
                    ['body', 'Body keyword'],
                    ['domain', 'Sender domain'],
                    ['any', 'Always (catch-all)'],
                  ] as const
                ).map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setMatchMode(value)}
                    className={cn(
                      'rounded-md border px-2 py-1.5 text-left text-xs transition-colors',
                      matchMode === value
                        ? 'border-indigo-500 bg-indigo-50 text-indigo-800'
                        : 'border-ovation-ink/10 hover:bg-ovation-ink/5',
                    )}
                  >
                    {label}
                  </button>
                ))}
              </div>

              {matchMode === 'ai' && (
                <select
                  value={classificationType}
                  onChange={(e) => setClassificationType(e.target.value)}
                  className="w-full rounded-lg border border-ovation-ink/15 px-3 py-2 text-sm"
                >
                  {CLASSIFICATION_PRESETS.map((p) => (
                    <option key={p.value} value={p.value}>
                      {p.label}
                    </option>
                  ))}
                  <option value="custom">Custom category (type below)</option>
                </select>
              )}

              {(matchMode === 'ai' && classificationType === 'custom') ||
              matchMode === 'subject' ||
              matchMode === 'body' ||
              matchMode === 'domain' ? (
                <input
                  required
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder={
                    matchMode === 'domain'
                      ? 'e.g. universalmusic.com'
                      : matchMode === 'body'
                        ? 'e.g. consumables'
                        : 'e.g. copyright'
                  }
                  className="w-full rounded-lg border border-ovation-ink/15 px-3 py-2 text-sm"
                />
              ) : null}
            </fieldset>

            <fieldset className="space-y-3 rounded-lg border border-ovation-ink/10 p-4">
              <legend className="px-1 text-xs font-medium uppercase tracking-wider text-ovation-muted">
                Then route to
              </legend>
              <select
                value={action}
                onChange={(e) => setAction(e.target.value as RuleAction)}
                className="w-full rounded-lg border border-ovation-ink/15 px-3 py-2 text-sm"
              >
                {RULE_ACTIONS.map((a) => (
                  <option key={a} value={a}>
                    {a.replace(/_/g, ' ')}
                  </option>
                ))}
              </select>

              {action === 'forward_to_role' && (
                <>
                  <div>
                    <select
                      value={targetDepartment}
                      onChange={(e) =>
                        setTargetDepartment((e.target.value || '') as Department | '')
                      }
                      className="w-full rounded-lg border border-ovation-ink/15 px-3 py-2 text-sm"
                    >
                      <option value="">Department cascade (optional)</option>
                      {HUMAN_DEPARTMENTS.map((d) => (
                        <option key={d.value} value={d.value}>
                          {d.label}
                        </option>
                      ))}
                    </select>
                    <p className="mt-1 text-xs text-ovation-muted">
                      Routes to the department head, then group email, then everyone in the
                      department.
                    </p>
                    <button
                      type="button"
                      onClick={() => setShowCreateDept(true)}
                      className="mt-1 text-xs font-medium text-indigo-600 hover:text-indigo-800"
                    >
                      + Create department
                    </button>
                  </div>

                  <div>
                    <select
                      value={targetPersonId}
                      onChange={(e) => setTargetPersonId(e.target.value)}
                      className="w-full rounded-lg border border-ovation-ink/15 px-3 py-2 text-sm"
                    >
                      <option value="">Pin to a specific person (optional)</option>
                      {pinnablePeople.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                          {p.title ? ` — ${p.title}` : ''}
                        </option>
                      ))}
                    </select>
                    <p className="mt-1 text-xs text-ovation-muted">
                      Overrides the department cascade — always goes straight to this person.
                    </p>
                    <button
                      type="button"
                      onClick={() => setShowCreatePerson(true)}
                      className="mt-1 text-xs font-medium text-indigo-600 hover:text-indigo-800"
                    >
                      + Create person
                    </button>
                  </div>

                  <div>
                    <label className="mb-1 block text-xs font-medium text-ovation-muted">
                      Legacy role fallback
                    </label>
                    <select
                      value={targetRole}
                      onChange={(e) => setTargetRole(e.target.value as TeamRole)}
                      className="w-full rounded-lg border border-ovation-ink/15 px-3 py-2 text-sm"
                    >
                      {TEAM_ROLES.map((r) => (
                        <option key={r} value={r}>
                          {r.replace(/_/g, ' ')}
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              )}

              {action === 'forward_to_email' && (
                <input
                  type="email"
                  required
                  value={targetEmail}
                  onChange={(e) => setTargetEmail(e.target.value)}
                  placeholder="person@example.com"
                  className="w-full rounded-lg border border-ovation-ink/15 px-3 py-2 text-sm"
                />
              )}
            </fieldset>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1 block text-xs font-medium text-ovation-muted">
                  Priority
                </label>
                <input
                  type="number"
                  value={priority}
                  onChange={(e) => setPriority(Number(e.target.value))}
                  className="w-full rounded-lg border border-ovation-ink/15 px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-ovation-muted">
                  Mailbox scope
                </label>
                <select
                  value={mailboxId}
                  onChange={(e) => setMailboxId(e.target.value)}
                  className="w-full rounded-lg border border-ovation-ink/15 px-3 py-2 text-sm"
                >
                  <option value="">All mailboxes</option>
                  {mailboxes.map((mb) => (
                    <option key={mb.id} value={mb.id}>
                      {mb.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
                className="rounded border-ovation-ink/20"
              />
              Rule enabled
            </label>

            {error && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {error}
              </div>
            )}
          </div>

          <div className="flex gap-2 border-t border-ovation-ink/10 px-5 py-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 rounded-lg border border-ovation-ink/15 px-4 py-2 text-sm font-medium hover:bg-ovation-ink/5"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 rounded-lg bg-ovation-ink px-4 py-2 text-sm font-medium text-ovation-paper hover:bg-ovation-ink/90 disabled:opacity-50"
            >
              {submitting ? 'Saving…' : isEdit ? 'Save changes' : 'Create rule'}
            </button>
          </div>
        </form>
      </aside>

      {showCreateDept && (
        <CreateDepartmentModal
          open={showCreateDept}
          onClose={() => setShowCreateDept(false)}
          onCreated={(dept) => {
            invalidateDirectory()
            if (dept.mapsToOrgDepartment) setTargetDepartment(dept.mapsToOrgDepartment)
            setShowCreateDept(false)
          }}
        />
      )}
      {showCreatePerson && (
        <CreatePersonModal
          open={showCreatePerson}
          departments={directoryDepartments}
          onClose={() => setShowCreatePerson(false)}
          onCreated={(person: DirectoryPerson) => {
            invalidateDirectory()
            if (person.scope === 'artist') {
              setTargetPersonId(person.id)
            } else if (person.departments.length > 0) {
              setTargetDepartment(person.departments[0] as Department)
            }
            setShowCreatePerson(false)
          }}
        />
      )}
    </div>
  )
}
